import {
  DEAL_WON_STATUSES,
  INSTALLMENT_BILLING,
  RECURRING_BILLING,
  WORKING_DAYS_PER_MONTH,
} from '../../constants';
import type { Deal, DealLine } from '../../types/domain';
import { contractMonthsInYear, contractTotalMonths } from '../dates';
import { toNumber } from '../numbers';
import { estimatedOpenMonths } from './contractLength';
import { dealAttachmentYear } from './dealYear';
import { hourlyEstimatedMonthly } from './hourly';
import { activeLines, lineBudgetMin } from './lines';
import { regieEstimated } from './regie';
import { _soldMonthlyAmount, dealDecisionYear } from './soldRevenue';
import type { Year } from './types';

// Montant mensuel "estimé" d'une ligne récurrente : pour la maintenance, utilise
// montant_mensuel_estime s'il est renseigné, sinon repli sur le montant vendu déjà connu
// (ligne déjà signée sans estimé jamais saisi). Hébergement : pas d'estimé séparé (décision
// actée), le montant mensuel sert directement aux deux.
export function _estimatedMonthlyAmount(l: DealLine): number {
  const fact = l.facturation || 'forfait_brique';
  // Forfait échelonné : budget estimé (borne basse, sinon vendu) ÷ nombre de mensualités.
  if (fact === INSTALLMENT_BILLING) {
    const months =
      contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue) || estimatedOpenMonths(l);
    return _lineEstimatedOneShot(l) / months;
  }
  if (fact === 'maintenance_corrective' || fact === 'maintenance_evolutive') {
    return l.montant_mensuel_estime != null && l.montant_mensuel_estime !== ''
      ? toNumber(l.montant_mensuel_estime) || 0
      : toNumber(l.montant_mensuel) || 0;
  }
  // Régie : sans estimé saisi, repli sur le mensuel vendu (même règle que la maintenance).
  if (fact === 'regie_horaire') {
    const filled = [l.heures_estime, l.taux_horaire_estime].every((v) => v != null && v !== '');
    return filled ? hourlyEstimatedMonthly(l) : _soldMonthlyAmount(l);
  }
  if (fact === 'regie_temporelle') {
    if (l.nb_devs_estime == null || l.nb_devs_estime === '') return _soldMonthlyAmount(l);
    const nde = toNumber(l.nb_devs_estime || 0),
      tjm = toNumber(l.tjm || 0);
    return nde * tjm * WORKING_DAYS_PER_MONTH;
  }
  return toNumber(l.montant_mensuel) || 0;
}

// Estimé d'une ligne ponctuelle (forfait) : borne basse du budget estimé, à défaut le budget vendu
// (même repli que la maintenance, qui reprend son mensuel vendu).
export function _lineEstimatedOneShot(l: DealLine): number {
  return lineBudgetMin(l) || toNumber(l.budget_vendu) || 0;
}

// Valeur estimée d'une ligne récurrente sur toute sa durée : dates du devis (12 mois à partir du
// début si la fin manque), sinon engagement estimé, sinon 12 mois (hébergement compris) — un même
// potentiel partout (liste, accueil, dashboard « Toutes les années »), égal à la somme des années.
export function _lineEstimatedRevenueTotal(l: DealLine): number {
  if (l.facturation === 'regie_temporelle' && l.date_debut_prevue) return regieEstimated(l);
  const mensuel = _estimatedMonthlyAmount(l);
  if (l.date_debut_prevue)
    return (
      mensuel *
      (contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue) || estimatedOpenMonths(l))
    );
  return mensuel * estimatedOpenMonths(l);
}

// Estimé d'une ligne pour une année. Récurrent daté : mensuel estimé × mois du contrat dans
// l'année (dates de la ligne vendue) ; récurrent sans date : valeur totale sur l'année de
// rattachement de l'affaire. Forfait : borne basse en entier sur l'année de signature pour une
// affaire gagnée (aucune année sans signature), sur l'année de rattachement sinon.
export function _lineEstimatedRevenueForYear(l: DealLine, year: Year, a: Deal): number {
  if (l.facturation === 'regie_temporelle' && l.date_debut_prevue) return regieEstimated(l, year);
  if (RECURRING_BILLING.includes(l.facturation as string)) {
    if (l.date_debut_prevue)
      return (
        _estimatedMonthlyAmount(l) *
        contractMonthsInYear(l.date_debut_prevue, l.date_fin_prevue, year, estimatedOpenMonths(l))
      );
    return dealAttachmentYear(a) === String(year) ? _lineEstimatedRevenueTotal(l) : 0;
  }
  const oneShotYear = DEAL_WON_STATUSES.includes(a.statut as string)
    ? dealDecisionYear(a)
    : dealAttachmentYear(a);
  return oneShotYear === String(year) ? _lineEstimatedOneShot(l) : 0;
}

// Valeur "estimée" d'une affaire (potentiel, tant que le devis n'est pas signé) : lignes
// récurrentes → valeur totale estimée ; lignes one-shot → borne basse du budget. Jamais
// proratisée par année : le potentiel d'une affaire est le même sur tous les écrans.
export function dealEstimatedRevenue(a: Deal): number {
  return activeLines(a).reduce(
    (s, l) =>
      s +
      (RECURRING_BILLING.includes(l.facturation as string)
        ? _lineEstimatedRevenueTotal(l)
        : _lineEstimatedOneShot(l)),
    0,
  );
}

// Estimé d'une affaire pour une année (dashboard sur une année), ou total si year === 'all'.
export function dealEstimatedRevenueForYear(a: Deal, year: Year): number {
  if (year === 'all') return dealEstimatedRevenue(a);
  return activeLines(a).reduce((s, l) => s + _lineEstimatedRevenueForYear(l, year, a), 0);
}
